import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DriverShell from "../components/DriverShell";
import { authenticatedApi } from "../lib/api";
import { getDriverSession, registerDriverPushToken, saveDriverSession } from "../lib/driverSession";

export default function DriverDashboard() {
  const driver = getDriverSession();
  const [profile, setProfile] = useState(null);
  const [foodOrders, setFoodOrders] = useState(null);
  const [serviceOrders, setServiceOrders] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const loadInFlight = useRef(false);
  const hasLoadedData = useRef(false);
  const servicePermissions = profile?.services || driver?.services || { moto: true, taxi: false };
  const enabledServiceTypes = [
    ...(servicePermissions.moto !== false ? ["DELIVERY"] : []),
    ...(servicePermissions.taxi === true ? ["TAXI"] : []),
  ];

  async function load() {
    if (!driver?.auth_token || loadInFlight.current) return;
    loadInFlight.current = true;
    try {
      const profileRequest = authenticatedApi("/delivery/update-user-info", driver.auth_token, {
        method: "POST", body: "{}", retry: 2,
      }).then((value) => {
        setProfile(value.data);
        saveDriverSession({ ...driver, ...value.data });
        registerDriverPushToken({ ...driver, ...value.data });
        hasLoadedData.current = true;
        return value;
      });
      const foodRequest = authenticatedApi("/delivery/get-delivery-orders", driver.auth_token, {
        method: "POST", body: "{}", retry: 2,
      }).then((value) => {
        setFoodOrders(value);
        hasLoadedData.current = true;
        setError("");
        return value;
      });
      const serviceResponse = enabledServiceTypes.length
        ? authenticatedApi("/delivery/service-orders", driver.auth_token, {
          method: "POST",
          body: JSON.stringify({ service_types: enabledServiceTypes }),
          retry: 1,
          timeoutMs: 8000,
        })
        : Promise.resolve({ new_orders: [], accepted_orders: [], pickedup_orders: [] });
      const serviceRequest = serviceResponse.then((value) => {
        setServiceOrders(value);
        hasLoadedData.current = true;
        return value;
      });
      const [profileResult, foodResult, servicesResult] = await Promise.allSettled([
        profileRequest, foodRequest, serviceRequest,
      ]);
      const anySuccess = [profileResult, foodResult, servicesResult].some((result) => result.status === "fulfilled");
      if (anySuccess) {
        hasLoadedData.current = true;
        setError("");
      } else if (!hasLoadedData.current) {
        setError("Conexión inestable. Seguimos intentando actualizar tu jornada.");
      }
    } finally {
      loadInFlight.current = false;
    }
  }

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, [driver?.auth_token]);

  const counts = useMemo(() => ({
    new: (foodOrders?.new_orders?.length || 0) + (serviceOrders?.new_orders?.length || 0),
    active:
      (foodOrders?.accepted_orders?.length || 0) +
      (foodOrders?.pickedup_orders?.length || 0) +
      (serviceOrders?.accepted_orders?.length || 0) +
      (serviceOrders?.pickedup_orders?.length || 0),
  }), [foodOrders, serviceOrders]);

  async function toggleStatus() {
    setBusy(true);
    try {
      const response = await authenticatedApi("/delivery/toggle-delivery-guy-status", driver.auth_token, {
        method: "POST",
        body: JSON.stringify({ toggle_status: true }),
      });
      setProfile(response.data);
      saveDriverSession({ ...driver, ...response.data });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  const commission = profile?.commission_account || foodOrders?.commission_account;

  return (
    <DriverShell>
      <section className="driver-welcome">
        <div><small>HOLA, {profile?.name || driver?.name}</small><h1>Tu jornada de hoy</h1></div>
        <button className={profile?.status ? "is-online" : ""} disabled={busy} onClick={toggleStatus} type="button">
          {profile?.status ? "● Disponible" : "○ Desconectado"}
        </button>
      </section>
      {error && <div className="food-message food-message--error"><b>{error}</b></div>}
      <section className="driver-metrics">
        <article><small>NUEVOS</small><strong>{counts.new}</strong><span>Pedidos, envíos y viajes</span></article>
        <article><small>EN CURSO</small><strong>{counts.active}</strong><span>Servicios asignados</span></article>
        <article><small>GANANCIA NETA HOY</small><strong>S/ {Number(commission?.earnings_today_net || 0).toFixed(2)}</strong><span>Después de comisión</span></article>
        <article><small>CALIFICACIÓN</small><strong>★ {profile?.averageRating || "0.0"}</strong><span>Opiniones de clientes</span></article>
      </section>
      {commission?.blocked && (
        <section className="driver-blocked">
          <b>Tu cuenta alcanzó el límite de comisión.</b>
          <span>Registra un pago para volver a aceptar servicios.</span>
          <Link to="/delivery/commissions">Ver comisiones</Link>
        </section>
      )}
      <section className="driver-summary-card">
        <div><h2>Servicios disponibles</h2><span>El contador incluye comida, envíos y taxi.</span></div>
        <strong>{counts.new}</strong>
        <Link to="/delivery/orders">Abrir servicios →</Link>
      </section>
    </DriverShell>
  );
}
